import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary, ThemeProvider } from '@zuruflex/ui';
import { RouterProvider } from 'react-router-dom';
import './i18n.js';
import './index.css';
import { AuthProvider } from './lib/auth.js';
import { PortalFlags } from './lib/flags.js';
import { router } from './router.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

/**
 * The seeded logins, floating over every screen while developing.
 *
 * Loaded this way on purpose: `import.meta.env.DEV` folds to `false` in a
 * production build, taking the dynamic import — and the demo passwords
 * inside it — out of the bundle entirely.
 */
const DevLogins = import.meta.env.DEV
  ? lazy(() => import('./components/DevLogins.js').then((m) => ({ default: m.DevLogins })))
  : null;

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    {/* The router carries its own `errorElement`; this catches the
        layer above it — the query client, the theme, the session —
        where a throw would otherwise leave a blank document. */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider themeColor={{ light: '#ffffff', dark: '#121213' }}>
          <AuthProvider>
            {/* Inside the session, because who is signed in is part of
                what a flag is worth; outside the router, so the
                maintenance banner is above every page it has. */}
            <PortalFlags>
              <RouterProvider router={router} />
            </PortalFlags>
            {DevLogins ? (
              <Suspense fallback={null}>
                <DevLogins />
              </Suspense>
            ) : null}
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
